import { cva } from "class-variance-authority"
import { TOUCH_TARGET_CLASS } from "@/v2-ui/utils/utils.touch.constants"

/**
 * Button base variants configuration using class-variance-authority (CVA).
 *
 * This defines the core styles and behavioral variants for the button component.
 * It supports customization via `variant`, `size`, and `isSquare` options,
 * along with compound rules for square buttons.
 *
 * Variants:
 * - `variant`: Visual style of the button.
 *   - `primary`: Solid button with primary background and foreground.
 *   - `destructive`: Red background to indicate destructive actions.
 *   - `success`: Green background to indicate positive/restorative actions.
 *   - `outline`: Transparent background with border and hover effects.
 *   - `secondary`: Muted background and text for less prominent actions.
 *   - `ghost`: Transparent button with hover background and text color.
 *   - `link`: Styled as a link with underline on hover.
 *   - `ghost-no-hover`: Transparent with only text color on hover (no background).
 *
 * - `size`: Controls padding, height, and text size. Can be used with or without `isSquare`.
 *   - `tiny`, `xs`, `sm`, `md`, `lg`, `xl`: Progressive sizes from smallest to largest.
 *   - `fit`: Deprecated. Use utility classes or `isSquare` instead.
 *
 * - `isSquare`: When `true`, forces the button to be a perfect square. Width matches height.
 *
 * Touch targets:
 * - Under `pointer: coarse` (finger, stylus) every size below 44 px is lifted to
 *   `TOUCH_TARGET_CLASS` — Apple's 44 pt minimum. The rule is a media query, so
 *   a mouse keeps the dense desktop sizes. Padding and font size are untouched;
 *   only the hit area grows.
 *
 * Compound Variants:
 * - When `isSquare` is `true`, fixed width, min-width, and min-height are applied
 *   based on the selected `size` to enforce a square aspect ratio.
 *
 * Default Variants:
 * - `variant`: `"primary"`
 * - `size`: `"md"`
 * - `isSquare`: `false`
 */
const _size_lookup = {
  tiny: "6", //24px
  xs: "7", //28px
  sm: "8", //32px
  md: "9", //36px
  lg: "10", //40px
  xl: "12", //48px
}

export const buttonBaseVariants = cva(
  "inline-flex items-center justify-center rounded-md whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        success: "bg-green-600 text-white shadow-sm hover:bg-green-700",
        warning: "bg-amber-500 text-white shadow-sm hover:bg-amber-600",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "ghost-no-hover": "hover:text-accent-foreground",
      },
      size: {
        tiny: `h-${_size_lookup.tiny} px-2 text-sm rounded-sm ${TOUCH_TARGET_CLASS}`,
        xs: `h-${_size_lookup.xs} px-2 text-sm rounded-sm ${TOUCH_TARGET_CLASS}`,
        sm: `h-${_size_lookup.sm} px-3 text-sm ${TOUCH_TARGET_CLASS}`,
        md: `h-${_size_lookup.md} px-3 text-sm ${TOUCH_TARGET_CLASS}`,
        lg: `h-${_size_lookup.lg} px-4 text-base ${TOUCH_TARGET_CLASS}`,
        xl: `h-${_size_lookup.xl} px-4 text-base`,
        fit: "h-fit text-xs", // deprecated
      },
      isSquare: {
        true: "overflow-hidden",
        false: "",
      },
    },
    compoundVariants: [
      {
        size: "tiny",
        isSquare: true,
        class: `w-${_size_lookup.tiny} min-w-${_size_lookup.tiny} min-h-${_size_lookup.tiny}`,
      },
      {
        size: "xs",
        isSquare: true,
        class: `w-${_size_lookup.xs} min-w-${_size_lookup.xs} min-h-${_size_lookup.xs}`,
      },
      {
        size: "sm",
        isSquare: true,
        class: `w-${_size_lookup.sm} min-w-${_size_lookup.sm} min-h-${_size_lookup.sm}`,
      },
      {
        size: "md",
        isSquare: true,
        class: `w-${_size_lookup.md} min-w-${_size_lookup.md} min-h-${_size_lookup.md}`,
      },
      {
        size: "lg",
        isSquare: true,
        class: `w-${_size_lookup.lg} min-w-${_size_lookup.lg} min-h-${_size_lookup.lg}`,
      },
      {
        size: "xl",
        isSquare: true,
        class: `w-${_size_lookup.xl} min-w-${_size_lookup.xl} min-h-${_size_lookup.xl}`,
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      isSquare: false,
    },
  },
)
